import * as React from 'react';
import styled from 'styled-components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { SidebarSubMenuItem } from '../molecules';
import {
  SubMenuLink,
  settingsSetupLinks,
} from '../constants/menu';
import DropdownButton from '../molecules/DropdownButton';

// How far each chevron click nudges the row. A little under a typical tab
// width so a click never skips a whole tab out of view.
const SCROLL_STEP = 160;

// Ignore sub-pixel rounding when deciding whether an edge still has content.
const EDGE_EPSILON = 2;

const SubMenuHeadLine = ({
  menuLinks,
  className,
  basePath,
  isSuperAdmin,
}: SubMenuHeadLineTypes) => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(
    false,
  );
  const [
    canScrollRight,
    setCanScrollRight,
  ] = React.useState(false);

  // Recompute which edges have hidden content. Called on scroll, on resize
  // (zoom changes the element's clientWidth), and when the tab list changes.
  const updateEdges = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > EDGE_EPSILON);
    setCanScrollRight(
      scrollLeft + clientWidth < scrollWidth - EDGE_EPSILON,
    );
  }, []);

  React.useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return undefined;

    // ResizeObserver catches browser zoom and window resizes, which change
    // how many tabs fit without firing a scroll event.
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateEdges)
        : null;
    ro?.observe(el);
    window.addEventListener('resize', updateEdges);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges, menuLinks]);

  const nudge = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({
      left: direction * SCROLL_STEP,
      behavior: 'smooth',
    });
  };

  return (
    <Wrapper className={className}>
      <EdgeButton
        type="button"
        $side="left"
        $visible={canScrollLeft}
        aria-hidden={!canScrollLeft}
        tabIndex={canScrollLeft ? 0 : -1}
        aria-label="Scroll tabs left"
        onClick={() => nudge(-1)}
      >
        <ChevronLeftIcon fontSize="small" />
      </EdgeButton>

      <Scroller
        ref={scrollerRef}
        role="tablist"
        onScroll={updateEdges}
        $fadeLeft={canScrollLeft}
        $fadeRight={canScrollRight}
      >
        {menuLinks?.map(element => {
          if (
            isSuperAdmin &&
            element.linkTo === 'report-setup'
          ) {
            return (
              <DropdownButton
                key={element.id}
                links={settingsSetupLinks}
                basePath={basePath}
              />
            );
          }
          return (
            <SidebarSubMenuItem
              key={element.id}
              to={element.linkTo}
              title={element.name}
              basePath={basePath}
            />
          );
        })}
      </Scroller>

      <EdgeButton
        type="button"
        $side="right"
        $visible={canScrollRight}
        aria-hidden={!canScrollRight}
        tabIndex={canScrollRight ? 0 : -1}
        aria-label="Scroll tabs right"
        onClick={() => nudge(1)}
      >
        <ChevronRightIcon fontSize="small" />
      </EdgeButton>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  /* EA-1409: use width:100% instead of flex:1. This component renders both
     inside HeadLine's horizontal flex row (where it must fill the width) and
     standalone as a direct child of a page's vertical flex column (Activities,
     Vocabulary). With flex:1, the column context made it grow VERTICALLY to
     ~viewport height, pushing the table far down the page (the loading spinner
     then appeared near the bottom). width:100% fills the width in both contexts
     and never stretches vertically, so the tab row stays at its content height. */
  width: 100%;
`;

const Scroller = styled.div<{
  $fadeLeft: boolean;
  $fadeRight: boolean;
}>`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  flex: 1;
  /* The settings tab row can hold up to 13 tabs. Zoom shrinks the effective
     viewport width, so past ~90% they no longer fit. Keep every tab reachable
     by scrolling the row horizontally rather than clipping the last tab(s).
     The chevron buttons + edge fades below are the visible affordance; the
     scrollbar itself is hidden so the nav bar stays clean. */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    height: 0;
    display: none;
  }

  /* Soft gradient cue that content continues past an edge. Only painted on the
     side that actually has hidden tabs. */
  mask-image: ${({ $fadeLeft, $fadeRight }) => {
    const left = $fadeLeft
      ? 'transparent 0, #000 24px'
      : '#000 0';
    const right = $fadeRight
      ? '#000 calc(100% - 24px), transparent 100%'
      : '#000 100%';
    return `linear-gradient(to right, ${left}, ${right})`;
  }};
  -webkit-mask-image: ${({ $fadeLeft, $fadeRight }) => {
    const left = $fadeLeft
      ? 'transparent 0, #000 24px'
      : '#000 0';
    const right = $fadeRight
      ? '#000 calc(100% - 24px), transparent 100%'
      : '#000 100%';
    return `linear-gradient(to right, ${left}, ${right})`;
  }};

  > * {
    flex-shrink: 0;
  }

  @media only screen and (max-width: 540px) {
    gap: 2px;
  }
`;

const EdgeButton = styled.button<{
  $side: 'left' | 'right';
  $visible: boolean;
}>`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  z-index: 1;
  display: ${({ $visible }) => ($visible ? 'inline-flex' : 'none')};
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-full);
  background: var(--neutral-0);
  color: var(--neutral-700);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: background-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--neutral-100);
    color: var(--neutral-900);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    font-size: 18px;
  }
`;

interface SubMenuHeadLineTypes {
  menuLinks: SubMenuLink[];
  className?: string;
  basePath?: string;
  isSuperAdmin?: boolean;
}

export default SubMenuHeadLine;
